import { useState, useCallback, useMemo } from "react";
import {
  calculateDistance,
  convertToMapboxFormat,
  getBibData,
} from "../utils/wayfinderUtils";
import { RouteSelectionService } from "../services/routeSelection";

// Route selection logic - updated for new data structure with 3 Ped Crossing routes
export const useWayfinderRouting = (bibData) => {
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [routeDistance, setRouteDistance] = useState(null);
  const [routeStartLocation, setRouteStartLocation] = useState(null);
  const [routeEndLocation, setRouteEndLocation] = useState(null);

  // Simple route cache to prevent duplicate processing (with size limit)
  const routeCache = useMemo(() => {
    const cache = new Map();
    // Set maximum cache size to prevent memory leaks
    const maxSize = 50;

    // Override set method to enforce size limit
    const originalSet = cache.set.bind(cache);
    cache.set = (key, value) => {
      if (cache.size >= maxSize) {
        // Remove oldest entry
        const firstKey = cache.keys().next().value;
        cache.delete(firstKey);
      }
      return originalSet(key, value);
    };

    return cache;
  }, []);

  // Initialize route selection service (use useMemo to prevent recreation)
  const routeSelectionService = useMemo(() => new RouteSelectionService(), []);

  // Process via points for optimal routing (similar to our previous logic)
  const processViaPointsForRouting = useCallback(
    (viaPoints, userLocation, assemblyCoords) => {
      if (!viaPoints || viaPoints.length === 0) return [];

      console.log(
        `🧠 [VIA POINT PROCESSING] Processing ${viaPoints.length} via points`
      );

      // Find nearest via point to user
      let nearestViaPoint = null;
      let minDistance = Infinity;

      for (let i = 0; i < viaPoints.length; i++) {
        const point = viaPoints[i];
        const distance = calculateDistance(
          userLocation[1], // lat
          userLocation[0], // lng
          point.latitude,
          point.longitude
        );

        if (distance < minDistance) {
          minDistance = distance;
          nearestViaPoint = { point, index: i };
        }
      }

      if (!nearestViaPoint) return [];

      console.log(
        `📍 [VIA POINT PROCESSING] Nearest via point: ${
          nearestViaPoint.index + 1
        } at ${minDistance.toFixed(3)}km`
      );

      // Build waypoints array: User → Via Points → Assembly
      let waypoints = [];

      // Add user's current location as first waypoint
      waypoints.push([userLocation[0], userLocation[1]]);

      // Smart via point selection: keep points that are far apart (meaningful for routing)
      // Skip points that are too close together (would create unnecessary detours)
      const startIndex = nearestViaPoint.index;
      const minDistanceBetweenPoints = 0.05; // 50m minimum distance between via points (reduced from 100m)

      let lastAddedIndex = startIndex;
      waypoints.push([
        viaPoints[startIndex].longitude,
        viaPoints[startIndex].latitude,
      ]);

      // Process remaining via points from nearest to assembly
      for (let i = startIndex + 1; i < viaPoints.length; i++) {
        // Safety check: ensure array bounds
        if (i >= viaPoints.length || lastAddedIndex >= viaPoints.length) {
          console.warn(
            "⚠️ [VIA POINT PROCESSING] Array bounds exceeded, stopping loop"
          );
          break;
        }

        const lastPoint = viaPoints[lastAddedIndex];
        const currentPoint = viaPoints[i];

        // Safety check: validate point data
        if (
          !lastPoint ||
          !currentPoint ||
          typeof lastPoint.latitude !== "number" ||
          typeof lastPoint.longitude !== "number" ||
          typeof currentPoint.latitude !== "number" ||
          typeof currentPoint.longitude !== "number"
        ) {
          console.warn(
            `⚠️ [VIA POINT PROCESSING] Invalid point data at index ${i}, skipping`
          );
          continue;
        }

        // Calculate distance from last added point to current point
        const distance = calculateDistance(
          lastPoint.latitude,
          lastPoint.longitude,
          currentPoint.latitude,
          currentPoint.longitude
        );

        // Smart filtering: consider both distance and route flow
        let shouldAdd = false;

        if (distance >= minDistanceBetweenPoints) {
          // Point is far enough - add it
          shouldAdd = true;
        } else if (distance >= 0.03) {
          // 30m minimum
          // Point is close but not too close - check if it's in the right direction
          const lastPoint = viaPoints[lastAddedIndex];

          // Safety check: ensure assembly point exists
          if (viaPoints.length === 0) {
            console.warn(
              "⚠️ [VIA POINT PROCESSING] No via points available for assembly check"
            );
            continue;
          }

          const assemblyPoint = viaPoints[viaPoints.length - 1]; // Last via point before assembly

          // Safety check: validate assembly point
          if (
            !assemblyPoint ||
            typeof assemblyPoint.latitude !== "number" ||
            typeof assemblyPoint.longitude !== "number"
          ) {
            console.warn(
              "⚠️ [VIA POINT PROCESSING] Invalid assembly point data"
            );
            continue;
          }

          // Calculate if this point moves us closer to assembly
          const lastToAssembly = calculateDistance(
            lastPoint.latitude,
            lastPoint.longitude,
            assemblyPoint.latitude,
            assemblyPoint.longitude
          );

          const currentToAssembly = calculateDistance(
            currentPoint.latitude,
            currentPoint.longitude,
            assemblyPoint.latitude,
            assemblyPoint.longitude
          );

          // Add if it doesn't take us significantly backwards
          if (currentToAssembly <= lastToAssembly + 0.05) {
            // Allow 50m backwards movement
            shouldAdd = true;
          }
        }

        if (shouldAdd) {
          waypoints.push([currentPoint.longitude, currentPoint.latitude]);
          lastAddedIndex = i;
          console.log(
            `📍 [VIA POINT PROCESSING] ✅ ADDED via point ${i} (distance: ${distance.toFixed(
              3
            )}km) - Smart added`
          );
        } else {
          console.log(
            `📍 [VIA POINT PROCESSING] ❌ SKIPPED via point ${i} (too close: ${distance.toFixed(
              3
            )}km) - Smart filtered`
          );
        }
      }

      console.log(
        `📍 [VIA POINT PROCESSING] Selected ${
          waypoints.length - 1
        } via points based on distance spacing`
      );

      // Safety check: ensure we don't exceed Mapbox's 25 waypoint limit
      if (waypoints.length > 23) {
        // 23 + user + assembly = 25 max
        console.warn(
          `⚠️ [VIA POINT PROCESSING] Too many waypoints (${waypoints.length}), truncating to 23`
        );
        waypoints = waypoints.slice(0, 23);
      }

      // Add assembly point as final destination
      waypoints.push([assemblyCoords[0], assemblyCoords[1]]);

      console.log(
        `🎯 [VIA POINT PROCESSING] Created ${waypoints.length} waypoints (${
          waypoints.length - 2
        } via points)`
      );

      // Validate route directness
      const routeDirectness = validateRouteDirectness(waypoints);
      console.log(
        `🎯 [VIA POINT PROCESSING] Route directness: ${routeDirectness.toFixed(
          1
        )}%`
      );

      return waypoints;
    },
    []
  );

  // Validate that the route is direct and doesn't have unnecessary detours
  const validateRouteDirectness = useCallback((waypoints) => {
    if (waypoints.length < 3) return 100; // Direct route

    // Calculate direct distance (start to end)
    const start = waypoints[0];
    const end = waypoints[waypoints.length - 1];
    const directDistance = calculateDistance(
      start[1], // lat
      start[0], // lng
      end[1], // lat
      end[0] // lng
    );

    // Calculate actual route distance through waypoints
    let routeDistance = 0;
    for (let i = 1; i < waypoints.length; i++) {
      const prev = waypoints[i - 1];
      const curr = waypoints[i];
      routeDistance += calculateDistance(
        prev[1], // lat
        prev[0], // lng
        curr[1], // lat
        curr[0] // lng
      );
    }

    // Calculate directness percentage (lower is more direct)
    const directness = (directDistance / routeDistance) * 100;

    if (directness < 70) {
      console.warn(
        `⚠️ [VIA POINT PROCESSING] Route has significant detours: ${directness.toFixed(
          1
        )}% directness`
      );
    }

    return directness;
  }, []);

  const getSelectedRoute = useCallback(
    (bibNumber) => {
      // Check cache first
      if (routeCache.has(bibNumber)) {
        console.log(`💾 [ROUTING] Using cached route for BIB: ${bibNumber}`);
        return routeCache.get(bibNumber);
      }

      console.log(`🔍 [ROUTING] Getting selected route for BIB: ${bibNumber}`);

      const data = bibData || getBibData(bibNumber);
      if (!data) {
        console.log("❌ [ROUTING] No BIB data found");
        return null;
      }

      // Use route selection service to find the best route
      // Pass BIB number for confirmation number-based distribution
      const selectedRoute = routeSelectionService.findBestRoute({
        ...data,
        bibNumber: bibNumber,
      });

      if (!selectedRoute) {
        console.log("❌ [ROUTING] No route selected by service");
        return null;
      }

      console.log("✅ [ROUTING] Route selected by service:", {
        assemblyPoint: selectedRoute.assemblyPoint,
        isBefore6: selectedRoute.isBefore6,
        hasViaPoints: !!selectedRoute.viaPoints,
        viaPointsCount: selectedRoute.viaPoints?.length || 0,
      });

      // Get route information
      const routeInfo = routeSelectionService.getRouteInfo(selectedRoute);

      const routeData = {
        id: `ROUTE-${bibNumber}`,
        name: `${routeInfo.assemblyPoint} Route`,
        coordinates: convertToMapboxFormat(data.assemblyCoordinates),
        routeInfo: routeInfo,
        isClosed: false,
        type: "Route",
        viaPoints: routeInfo.viaPoints,
        timeRestriction: routeInfo.timeRestriction,
        isCurrentlyAvailable: routeInfo.isCurrentlyAvailable,
      };

      // Cache the result
      routeCache.set(bibNumber, routeData);
      console.log(`💾 [ROUTING] Cached route for BIB: ${bibNumber}`);

      return routeData;
    },
    [bibData, routeSelectionService, routeCache]
  );

  const handleSmartRouting = useCallback(
    (userLocation, bibData, selectedRoute) => {
      if (!bibData || !userLocation) {
        console.warn("⚠️ [SMART ROUTING] Missing required data for routing");
        return;
      }

      // Prevent duplicate processing
      if (window.routeWaypoints && window.routeWaypoints.length > 0) {
        console.log(
          "🛣️ [SMART ROUTING] Route already processed, skipping duplicate call"
        );
        console.log(
          "🛣️ [SMART ROUTING] Current waypoints:",
          window.routeWaypoints
        );
        return;
      }

      // Clear any existing route data to ensure fresh processing
      window.routeWaypoints = null;

      // Add fallback timeout to prevent infinite processing
      const fallbackTimeout = setTimeout(() => {
        if (!window.routeWaypoints) {
          console.warn(
            "⚠️ [SMART ROUTING] Route processing timeout, creating fallback route"
          );
          // Create simple direct route as fallback
          const fallbackWaypoints = [
            [userLocation[0], userLocation[1]],
            [bibData.assemblyCoordinates[1], bibData.assemblyCoordinates[0]],
          ];
          window.routeWaypoints = fallbackWaypoints;
        }
      }, 5000); // 5 second timeout

      // Get converted coordinates for Mapbox format
      const assemblyCoords = convertToMapboxFormat(bibData.assemblyCoordinates);

      if (!assemblyCoords) {
        console.error("Invalid assembly coordinates in bibData");
        return;
      }

      // Use via-point routing: User → Via Points → Assembly
      console.log("🛣️ [SMART ROUTING] Using via-point routing to assembly");

      // Set the complete route: User → Via Points → Assembly
      setRouteStartLocation(userLocation);
      setRouteEndLocation(assemblyCoords);

      // Process via points for optimal routing
      if (selectedRoute.viaPoints && selectedRoute.viaPoints.length > 0) {
        console.log(
          `📍 [SMART ROUTING] Route has ${selectedRoute.viaPoints.length} via points for road-following`
        );

        // Process via points to create optimal waypoints
        const processedWaypoints = processViaPointsForRouting(
          selectedRoute.viaPoints,
          userLocation,
          assemblyCoords
        );

        // Store processed waypoints for RouteSource component (with safety check)
        if (processedWaypoints && processedWaypoints.length > 0) {
          console.log(
            "🛣️ [SMART ROUTING] Setting window.routeWaypoints:",
            processedWaypoints
          );
          window.routeWaypoints = processedWaypoints;
          // Clear timeout since we successfully processed the route
          clearTimeout(fallbackTimeout);
        } else {
          console.warn(
            "⚠️ [SMART ROUTING] No valid waypoints generated, clearing route data"
          );
          window.routeWaypoints = null;
        }
        console.log(
          `🎯 [SMART ROUTING] Processed ${processedWaypoints.length} waypoints for routing`
        );
      } else {
        console.log(
          "⚠️ [SMART ROUTING] No via points available, will use direct route"
        );
        // Clear timeout since we're using direct route
        clearTimeout(fallbackTimeout);
      }
    },
    []
  );

  const handleDirectionsClick = useCallback(
    (userLocation, bibData, selectedRoute, showDirections) => {
      const newShowDirections = !showDirections;

      if (newShowDirections) {
        // Get converted coordinates for Mapbox format
        const assemblyCoords = convertToMapboxFormat(
          bibData.assemblyCoordinates
        );

        if (!assemblyCoords) {
          console.error("Invalid assembly coordinates in bibData");
          return;
        }

        // Use smart routing logic instead of simple route
        handleSmartRouting(userLocation, bibData, selectedRoute);

        // With via-point routing, we always go directly to assembly
        // No need for two-stage routing logic anymore
      } else {
        // Reset route distance when hiding directions
        setRouteDistance(null);
        setRouteStartLocation(null);
        setRouteEndLocation(null);
        // Clear waypoints to allow fresh processing when showing route again
        window.routeWaypoints = null;
      }

      return newShowDirections;
    },
    [handleSmartRouting]
  );

  return {
    selectedRoute,
    setSelectedRoute,
    routeDistance,
    setRouteDistance,
    routeStartLocation,
    setRouteStartLocation,
    routeEndLocation,
    setRouteEndLocation,

    getSelectedRoute,
    handleSmartRouting,
    handleDirectionsClick,
  };
};
